import { router, useForm } from "@inertiajs/react"
import { PlusIcon, BookOpenIcon, ClockIcon } from "lucide-react"
import { type ReactNode, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { FieldError } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { GuideRow, GuideType, Season } from "@/lib/types"

interface PropertySummary {
  id: number
  name: string
  region_id: number | null
}

interface Props {
  property: PropertySummary
  guides: GuideRow[]
  has_default: boolean
  urls: {
    index: string
    create: string
    create_default: string
  }
}

const GUIDE_TYPE_LABELS: Record<GuideType, string> = {
  regional_base:      "Regional Base",
  property_default:   "Property Default",
  seasonal_template:  "Seasonal",
  guest_personalized: "Guest Personalized",
}

const STATUS_VARIANTS: Record<string, "default" | "outline" | "secondary"> = {
  active:   "default",
  draft:    "outline",
  archived: "secondary",
}

// C4 fix: keyboard-accessible card using a <button> wrapper
function GuideCard({ guide }: { guide: GuideRow }) {
  return (
    <Card
      role="button"
      tabIndex={0}
      className="cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => router.get(guide.show_url)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          router.get(guide.show_url)
        }
      }}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base font-medium leading-tight">{guide.title}</CardTitle>
          <Badge variant={STATUS_VARIANTS[guide.status] ?? "outline"} className="shrink-0 text-xs capitalize">
            {guide.status}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{GUIDE_TYPE_LABELS[guide.guide_type]}</p>
      </CardHeader>
      {guide.season && (
        <CardContent className="pb-2">
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground capitalize">
            <ClockIcon className="size-3" aria-hidden />
            {guide.season}
          </span>
        </CardContent>
      )}
      <CardFooter className="justify-between pt-0">
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <BookOpenIcon className="size-3" aria-hidden />
          {guide.items_count} {guide.items_count === 1 ? "item" : "items"}
        </span>
        <span className="text-xs text-muted-foreground">
          {new Date(guide.updated_at).toLocaleDateString()}
        </span>
      </CardFooter>
    </Card>
  )
}

interface CreateFormData {
  title: string
  guide_type: GuideType // admin create is seasonal-only (fixed); never unset
  season: Season | "all"
}

// C1 fix: key prop forces remount (and useForm reset) on each open
function CreateSheetInner({ onClose, createUrl }: { onClose: () => void; createUrl: string }) {
  // Admin create makes seasonal_template guides only (property_default is
  // onboarding-owned — PR4); guide_type is fixed, no base_guide_id.
  const { data, setData, post, processing, errors, transform } = useForm<CreateFormData>({
    title:      "",
    guide_type: "seasonal_template",
    season:     "all",
  })

  transform((d) => ({
    guide: {
      title:      d.title,
      guide_type: d.guide_type,
      season:     d.season === "all" ? null : d.season,
    },
  }))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(createUrl, { onSuccess: onClose })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
      <div className="grid gap-1.5">
        <Label htmlFor="guide-title">
          Title <span aria-hidden>*</span>
        </Label>
        <Input
          id="guide-title"
          value={data.title}
          onChange={(e) => setData("title", e.target.value)}
          required
          aria-required="true"
        />
        <FieldError>{errors.title}</FieldError>
      </div>

      {/* Admin create is seasonal-only; the guest guide (property_default) is
          onboarding-owned. guide_type is fixed to seasonal_template. */}
      <FieldError>{errors.guide_type}</FieldError>

      <div className="grid gap-1.5">
        <Label htmlFor="guide-season">Season</Label>
        <Select value={data.season} onValueChange={(v) => setData("season", v as Season | "all")}>
          <SelectTrigger id="guide-season">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All seasons</SelectItem>
            <SelectItem value="spring">Spring</SelectItem>
            <SelectItem value="summer">Summer</SelectItem>
            <SelectItem value="autumn">Autumn</SelectItem>
            <SelectItem value="winter">Winter</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={processing || !data.title.trim()}>
          {processing ? "Creating…" : "Create seasonal guide"}
        </Button>
      </div>
    </form>
  )
}

function CreateSheet({ open, onClose, createUrl }: { open: boolean; onClose: () => void; createUrl: string }) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Create seasonal guide</SheetTitle>
        </SheetHeader>
        {/* C1 fix: key remounts (and resets) the inner form on every open */}
        {open && <CreateSheetInner key={String(open)} onClose={onClose} createUrl={createUrl} />}
      </SheetContent>
    </Sheet>
  )
}

export default function GuidesIndex({ property, guides, has_default, urls }: Props) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [settingUp, setSettingUp] = useState(false)

  // Recovery: create the one nested guest guide (property_default) when a property
  // has none (onboarded with the default-guide step skipped). Onboarding normally
  // creates it; admin create only makes seasonal guides. Guard against a double POST
  // (the second would hit the server's "already exists" no-op and flash an error).
  function setUpGuestGuide() {
    router.post(urls.create_default, {}, {
      onStart: () => setSettingUp(true),
      onFinish: () => setSettingUp(false),
    })
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Guides</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
        </div>
        <div className="flex items-center gap-2">
          {!has_default && (
            <Button size="sm" onClick={setUpGuestGuide} disabled={settingUp}>
              <PlusIcon className="mr-1.5 size-4" aria-hidden />
              Set up guest guide
            </Button>
          )}
          <Button size="sm" variant={has_default ? "default" : "outline"} onClick={() => setSheetOpen(true)}>
            <PlusIcon className="mr-1.5 size-4" aria-hidden />
            New seasonal guide
          </Button>
        </div>
      </div>

      {guides.length === 0 ? (
        <div className="rounded-lg border border-dashed py-20 text-center text-muted-foreground">
          {has_default ? (
            <>
              <p className="text-sm">No seasonal guides yet.</p>
              <Button variant="link" size="sm" className="mt-1" onClick={() => setSheetOpen(true)}>
                Create a seasonal guide
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm">This property has no guest guide yet.</p>
              <Button variant="link" size="sm" className="mt-1" onClick={setUpGuestGuide} disabled={settingUp}>
                Set up the guest guide
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="guides-grid">
          {guides.map((g) => (
            <GuideCard key={g.id} guide={g} />
          ))}
        </div>
      )}

      <CreateSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        createUrl={urls.create}
      />
    </div>
  )
}

GuidesIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
